import { Form } from "@inertiajs/react"
import { Building2, Flame } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { completeOnboardingWelcomePath } from "@/routes"

/**
 * OKS-468 — Immersive "Mission accepted 🎉" success state (Figma `30:169`),
 * rendered full-bleed AFTER a successful Accept. Mirrors the S1-02 arrival
 * treatment: a brand cover background under a dark scrim, a centered logo-card
 * with a flame badge, post-based copy, and a single forward CTA.
 *
 * **Post-based streak framing (P0 domain invariant):** the copy is
 * "Post within 7 days to start your streak…" — it NEVER claims an active
 * streak. Accepting a mission does not start a streak; posting does.
 *
 * **Tour completion:** "Go to my missions" POSTs `completeOnboardingWelcomePath()`
 * (a forward exit) which stamps the membership's `tour_completed_at` and 303s
 * to crew home — so the handoff shows exactly once.
 *
 * The component is rendered OUTSIDE `WizardShell` so it escapes the shell's
 * centered padding and fills the viewport.
 *
 * Animation: motion-safe scrim fade-in + logo-card scale (≤480ms via
 * `--animate-brand-intro`); under `prefers-reduced-motion` everything is
 * visible immediately with no transform.
 */
export interface ImmersiveMissionAcceptedProps {
  brandName: string
  brandLogoUrl: string | null
  coverImageUrl: string | null
}

export function ImmersiveMissionAccepted({
  brandName,
  brandLogoUrl,
  coverImageUrl,
}: ImmersiveMissionAcceptedProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="welcome-immersive-success"
      className="relative flex min-h-dvh w-full flex-col items-center justify-between overflow-hidden px-6 py-10"
    >
      {/* Brand cover background. */}
      {coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
      ) : (
        <div
          className="from-primary/40 absolute inset-0 -z-10 bg-gradient-to-br to-neutral-900"
          aria-hidden="true"
        />
      )}
      {/* Dark scrim gradient — guarantees AA contrast for the white copy. */}
      <div
        className="motion-safe:animate-in motion-safe:fade-in absolute inset-0 -z-10 bg-gradient-to-b from-[#080d14]/55 to-[#03050a]/90"
        aria-hidden="true"
      />

      <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
        {/* Logo-card with flame badge. */}
        <div className="motion-safe:animate-brand-intro relative">
          <div className="bg-card flex size-32 items-center justify-center rounded-[1.875rem] shadow-2xl">
            <Avatar className="size-20">
              {brandLogoUrl ? (
                <AvatarImage src={brandLogoUrl} alt={brandName} />
              ) : null}
              <AvatarFallback className="bg-muted text-xl font-semibold">
                {brandInitials(brandName) || (
                  <Building2 className="size-8" aria-hidden="true" />
                )}
              </AvatarFallback>
            </Avatar>
          </div>
          <span
            className="border-background bg-primary absolute -right-1 -bottom-1 flex size-11 items-center justify-center rounded-full border-[3px]"
            aria-hidden="true"
          >
            <Flame className="text-primary-foreground size-5" />
          </span>
        </div>

        <div className="flex flex-col items-center gap-2 text-white">
          <h1
            className="text-2xl font-semibold"
            data-testid="welcome-immersive-success-title"
          >
            {t("wizard.welcome.accepted.title")}
          </h1>
          <p className="max-w-xs text-[0.9375rem] text-white/85">
            {t("wizard.welcome.accepted.body")}
          </p>
        </div>
      </div>

      {/* Forward exit — POST stamps the tour, 303 → crew home. */}
      <Form
        method="post"
        action={completeOnboardingWelcomePath()}
        className="w-full max-w-sm"
        data-testid="welcome-immersive-success-form"
      >
        {({ processing }) => (
          <Button
            type="submit"
            disabled={processing}
            aria-disabled={processing}
            size="lg"
            className="w-full"
            data-testid="welcome-go-to-missions"
          >
            {processing && <Spinner className="size-4" />}
            {t("wizard.welcome.accepted.cta")}
          </Button>
        )}
      </Form>
    </div>
  )
}

function brandInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((token) => token[0]?.toUpperCase() ?? "")
    .join("")
}

export default ImmersiveMissionAccepted
